import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components/macro";
import moment from "moment";
import { getEnvConfig } from "helpers/environment-config";
import { PrimaryButton, ButtonSizeOptions } from "@loanbossinc/component-library";
import { Typography } from "@material-ui/core";
import DatePicker from "material-ui-pickers/DatePicker";
import { LENDER_REPORTING_DASHBOARD } from "constants/routes";

import { TaskStatus } from "./constants";
import { CalendarButton } from "./calendarButton";
import { getLinkText, pushToPage } from "./lenderReportingDashboard.helper";
import { trackLCReportApprove } from "lib/mixpanel";
import DeleteReportInstanceDialog from "./deleteReportInstanceDialog";
import BorrowingEntitiesTooltip from "./borrowingEntitiesTooltip";

const ApproveButton = styled(PrimaryButton)`
  && {
    width: 262px;
    height: 48px;
    border-radius: 8px;
    background: ${({ props }) => props.bgColor};
    color: ${({ props }) => props.fontColor};
    border: ${({ props }) => props.border};
    &[disabled] {
      background: ${({ props }) => props.disabledBgColor};
      color: ${({ props }) => props.disabledFontColor};
    }
  }
`;

const AssigneeAndCalendarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const BorrowingEntities = styled.div`
  font-family: Work Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  color: #005aa9;
`;

const ButtonsExpirationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 2em;
`;

const ButtonText = styled.div`
  font-family: Work Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 19px;
`;

const ButtonWrapper = styled.div`
  && {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 19px;
    font-family: Work Sans;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    color: #005aa9;
    text-decoration: none;
    cursor: pointer;
  }
`;

const DatePickerWrapper = styled.div`
  height: 48px;
  padding-bottom: 21px;
`;

const DealName = styled.div`
  font-family: Work Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #000000;
`;

const DealNameFlexRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5em;
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5em;
`;

const ReportName = styled.div`
  font-family: Work Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 28px;
  line-height: 33px;
  display: flex;
  align-items: center;
  color: #000000;
`;

const TasksWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #dadce0;
  padding: 1em;
  margin: 0px -1em;
  min-height: 50px;
`;

const TitleApproveWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const TitleSubtitleWrapper = styled.div`
  height: 70%;
  display: flex;
  flex-direction: column;
  gap: 1em;
  justify-content: center;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #dadce0;
  width: calc(100% - (2em + 1px));
  background-color: #ffffff;
  padding: 4em 1em 0em 1em;
`;

const StyledTypography = styled(Typography)`
  && {
    font-family: "Work Sans";
    font-style: normal;
    font-weight: ${({ bolder }) => (bolder ? "500" : "400")};
    font-size: 16px;
    line-height: 19px;
    color: ${({ gray }) => gray && "#5F6368"};
  }
`;

const removeDuplicateBorrowEnts = tasks => tasks.reduce((uniqueBorrowEnts, task) => {
  task.deal.borrowingEntities.forEach(bE => {
    if (!uniqueBorrowEnts.includes(bE)) {
      uniqueBorrowEnts.push(bE);
    }
  });

  return uniqueBorrowEnts;
}, []);

const TaskHeader = ({ data, addTask, history, updateInstanceDetails, updateTasks, canApprove, canDeleteReportGroupInstance, deleteReportGroupInstance, authenticatedUser, company }) => {
  const { tasks } = data;
  const borrowingEntities = tasks && tasks.length > 0 ? removeDuplicateBorrowEnts(tasks).map(borrowingEntity => <div>{borrowingEntity}</div>) : [];
  const [shortenedReportName, setShortenedReportName] = useState("");
  const fullReportName = data.reportGroup?.reportName;
  const [assetManagerName, setAssetManagerName] = useState("");
  const assetManager = data.deal?.assetManager;

  useEffect(() => {
    if (fullReportName) {
      const splitReportName = fullReportName.split(" - ");
      const trimmedReportName = splitReportName[2].trim();
      setShortenedReportName(trimmedReportName);
    }
  }, [fullReportName]);

  useEffect(() => {
    if (assetManager) {
      setAssetManagerName(`${assetManager.firstName} ${assetManager.lastName}`);
    }
  }, [assetManager]);

  const handleDateChange = date => {
    const rgi = data.reportGroupInstance.Items[0];
    rgi.dueDate = new Date(date).toISOString();
    updateInstanceDetails({
      reportId: data.reportGroupInstance.Items[0].id,
      reportGroupInstance: rgi
    });
  };

  const getTaskCounterText = useCallback(() => {
    if (tasks) {
      const totalAmount = tasks.length;
      const completedAmount = tasks.filter(task => task.statusId === TaskStatus.COMPLETED).length;

      if (totalAmount === 0) {
        return "No tasks";
      } else if (totalAmount === completedAmount) {
        return "All tasks complete";
      }

      return `${completedAmount} out of ${totalAmount} ${totalAmount === 1 ? "task" : "tasks"} complete`;
    }

    return "No tasks";
  }, [tasks]);

  const allTasksContainAttachment = useCallback(() => {
    if (tasks) {
      return tasks.every(task => task.files && task.files.length > 0);
    }

    return false;
  }, [tasks]);

  const areAllTasksComplete = useCallback(() => {
    if (tasks) {
      return tasks.every(task => task.statusId === TaskStatus.COMPLETED);
    }

    return false;
  }, [tasks]);

  const isButtonDisabled = useCallback(() => {
    if(!areAllTasksComplete() && !allTasksContainAttachment()) {
        return true;
      }
    if (!canApprove && areAllTasksComplete()) {
      return true;
    }
    if (tasks && tasks.length === 0) {
      return true;
    }

    return false;
  }, [canApprove,areAllTasksComplete, tasks]);

  const buttonProps = useMemo(() => {
    const props = {
      variant: "contained",
      bgColor: "#005AA9",
      fontColor: "#FFFFFF",
      label: "",
      disabledBgColor: "#E5E5E5",
      disabledFontColor: "#FFFFFF",
      border: "none"
    };
    if (areAllTasksComplete()) {
      if (canApprove) {
        props.label = "Approve report";
      } else {
        props.disabledFontColor = "#5F6368";
        props.disabledBgColor = "#FFFFFF";
        props.label = "Waiting for approval";
        props.variant = "outlined";
        props.border = "1px solid #C4C4C4";
      }
    } 
    else if(allTasksContainAttachment()) {
      return { ...props, label: "Mark all tasks complete" };
    } else {
      props.label = "Mark all tasks complete";
    }

    return props;
  }, [canApprove, areAllTasksComplete, allTasksContainAttachment, tasks]);

  const showMissingMessage = tasks && tasks.length > 0 && !allTasksContainAttachment() && !areAllTasksComplete();

  const onClickHandler = () => {
    if (areAllTasksComplete() && canApprove) {
      // approve report
      const rgi = data.reportGroupInstance?.Items[0];
      if (rgi) {
        rgi.isApproved = true;
        rgi.isManuallyUnlocked = rgi.isManuallyUnlocked !== undefined ? rgi.isManuallyUnlocked : false;
        rgi.isSubmitted = rgi.isSubmitted !== undefined ? rgi.isSubmitted : false;
        rgi.approvalDate = new Date();
        rgi.approverContactId = authenticatedUser.id;
        rgi.approverCompanyId = company.id;
        if(!data.reportGroup.shouldCalculateDscr) {
          rgi.shouldIncludeDscrInReport = false;
        }
        if(!data.reportGroup.shouldCalculateDebtYield) {
          rgi.shouldIncludeDebtYieldInReport = false;
        }
        updateInstanceDetails({
          reportId: rgi.id,
          reportGroupInstance: rgi
        });
        trackLCReportApprove();
      }
    } else {
      // mark all tasks complete
      const updatedTasks = tasks.map(task => {
        if (task.statusId !== TaskStatus.COMPLETED) {
          return { ...task, statusId: TaskStatus.COMPLETED };
        }

        return task;
      });

      updateTasks({ taskArray: updatedTasks, reportingGroupInstanceId: data.reportGroupInstance?.Items[0]?.id });
    }
  };

  const [isDeleteReportGroupInstanceModalOpen, setIsDeleteReportGroupInstanceModalOpen] = useState(false);

  const confirmDeleteReport = async () => {
    const dealId = data.deal?.id;
    const reportGroupInstanceId = data.reportGroupInstance?.Items[0]?.id;
    
    await new Promise((resolve, reject) => {
      deleteReportGroupInstance({ reportGroupInstanceId, resolve, reject });
    });

    pushToPage(history, dealId, reportGroupInstanceId);
  };

  const goToNewDealsPage = async (dealId) => {
    const newFrontendUrl = await getEnvConfig("newFrontendUrl");
    const url = `${newFrontendUrl}/deals/${dealId}`;

    window.location.assign(url);
  };

  return (
    <Wrapper>
      <ButtonsExpirationWrapper>
        <FlexRow>
          <ButtonWrapper onClick={() => pushToPage(history, data.deal?.id, data.reportGroupInstance?.Items[0]?.id)}>
            <div className="material-icons" style={{ width: "24px" }}>
              arrow_backwards
            </div>
            <ButtonText>{getLinkText(history.location.state?.from)}</ButtonText>
          </ButtonWrapper>
          <ButtonWrapper>
            <div className="material-icons" style={{ width: "24px" }}>
              check_circle_outline
            </div>
            <ButtonText
              onClick={() => history.push(`/lenderReportingDashboard/submitted/${data?.reportGroupInstance?.Items[0].id}`, { from: history.location.state?.from })}
            >
              Submitted Reports
            </ButtonText>
          </ButtonWrapper>
        </FlexRow>
      </ButtonsExpirationWrapper>
      <TitleApproveWrapper>
        <TitleSubtitleWrapper>
          <DealNameFlexRow>
            <ButtonWrapper onClick={() => goToNewDealsPage(data.deal.newDealId)}>
              <div className="material-icons">open_in_new</div>
            </ButtonWrapper>
            <DealName>{data.deal?.name}</DealName>
            <BorrowingEntitiesTooltip entities={borrowingEntities}>
              <BorrowingEntities>(See borrowing entities)</BorrowingEntities>
            </BorrowingEntitiesTooltip>
          </DealNameFlexRow>
          <ReportName>{shortenedReportName}</ReportName>
        </TitleSubtitleWrapper>
        {canDeleteReportGroupInstance && (
          <>
            <ButtonWrapper onClick={() => setIsDeleteReportGroupInstanceModalOpen(true)} style={{ color: "#C40A0A", marginLeft: "auto" }}>
              <ButtonText>Delete report packet</ButtonText>
              <div className="material-icons" style={{ width: "24px" }}>
                delete_outline
              </div>
            </ButtonWrapper>
            <DeleteReportInstanceDialog
              modalOpen={isDeleteReportGroupInstanceModalOpen}
              handleCancel={() => setIsDeleteReportGroupInstanceModalOpen(false)}
              handleConfirm={confirmDeleteReport}
            />
          </>
        )}
      </TitleApproveWrapper>
      <AssigneeAndCalendarWrapper>
        <StyledTypography style={{ fontSize: 16 }}>
          Assigned to
          {" "}
          <b>{assetManagerName ? assetManagerName : <></>}</b>
        </StyledTypography>
        <DatePickerWrapper>
          <DatePicker
            TextFieldComponent={props => <CalendarButton {...props} />}
            style={{ width: "262px", height: "48px" }}
            label="Due"
            format="MMMM DD, YYYY"
            value={moment.utc(data?.reportGroupInstance?.Items[0] && data?.reportGroupInstance?.Items[0].dueDate)}
            onChange={value => handleDateChange(value)}
          />
        </DatePickerWrapper>
      </AssigneeAndCalendarWrapper>
      <TasksWrapper>
        <FlexRow>
          <StyledTypography gray bolder>
            {getTaskCounterText()}
          </StyledTypography>
          <ButtonWrapper onClick={addTask}>
            <div className="material-icons" style={{ width: "24px" }}>
              add
            </div>
            <ButtonText>Add Task</ButtonText>
          </ButtonWrapper>
        </FlexRow>
        {showMissingMessage ? (
          <StyledTypography gray bolder>
            Required attachments missing
          </StyledTypography>
        ) : (
          <ApproveButton
            label={buttonProps.label}
            disabled={isButtonDisabled()}
            size={ButtonSizeOptions.lgFluid}
            props={buttonProps}
            variant={buttonProps.variant}
            onClick={onClickHandler}
          />
        )}
      </TasksWrapper>
    </Wrapper>
  );
};

export default TaskHeader;
